import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { workApiFetch } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";
import type { WorkOrderId } from "../lib/workIds";

const attachmentSchema = z.object({
  attachmentId: z.number().int().positive(),
  workOrderId: z.string(),
  procedureId: z.string().uuid(),
  procedureVersion: z.number().int().positive(),
  snapshot: z.unknown(),
  created: z.boolean(),
});

const attachmentResponseSchema = z.object({
  attachment: attachmentSchema.nullable(),
});

export type ProcedureAttachment = z.infer<typeof attachmentSchema>;

export async function attachProcedureToWorkOrder(input: {
  customerId: string;
  facilityId: string;
  workOrderId: WorkOrderId;
  procedureId: string;
}) {
  const response = await workApiFetch(
    path(input.customerId, input.facilityId, input.workOrderId),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ procedureId: input.procedureId }),
    },
  );
  return attachmentSchema.parse(await parseResponse(response));
}

export async function updateProcedureAttachment(input: {
  customerId: string;
  facilityId: string;
  workOrderId: WorkOrderId;
  procedureId: string | null;
  discardResponses: boolean;
  expectedAttachmentId: number | null;
}) {
  const response = await workApiFetch(
    path(input.customerId, input.facilityId, input.workOrderId),
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        procedureId: input.procedureId,
        discardResponses: input.discardResponses,
        expectedAttachmentId: input.expectedAttachmentId,
      }),
    },
  );
  return attachmentResponseSchema.parse(await parseResponse(response))
    .attachment;
}

function path(
  customerId: string,
  facilityId: string,
  workOrderId: WorkOrderId,
) {
  return `/v1/customers/${encodeURIComponent(customerId)}/facilities/${encodeURIComponent(facilityId)}/work-orders/${encodeURIComponent(workOrderId)}/procedure`;
}

async function parseResponse(response: Response) {
  const payload: unknown = await response.json();
  if (!response.ok) {
    const message =
      typeof payload === "object" &&
      payload !== null &&
      "error" in payload &&
      typeof payload.error === "string"
        ? payload.error
        : `Request failed (${response.status})`;
    const error = new Error(message);
    const code =
      typeof payload === "object" &&
      payload !== null &&
      "code" in payload &&
      typeof payload.code === "string"
        ? payload.code
        : undefined;
    Object.assign(error, { status: response.status, code });
    throw error;
  }
  return payload;
}

export function useProcedureAttachmentQuery(workOrderId: WorkOrderId) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const customerId = selectedCustomer?.id;
  const facilityId = selectedFacility?.id;
  return useQuery({
    queryKey: ["work-order-procedure", customerId, facilityId, workOrderId],
    queryFn: async () => {
      if (!customerId || !facilityId)
        throw new Error("A customer and facility must be selected.");
      return attachmentResponseSchema.parse(
        await parseResponse(
          await workApiFetch(path(customerId, facilityId, workOrderId)),
        ),
      ).attachment;
    },
    enabled: Boolean(customerId && facilityId && workOrderId),
    meta: { errorMessage: "Failed to load the attached Procedure." },
  });
}

export function useAttachProcedureMutation(workOrderId: WorkOrderId) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (procedureId: string) => {
      if (!selectedCustomer || !selectedFacility)
        throw new Error("A customer and facility must be selected.");
      return attachProcedureToWorkOrder({
        customerId: selectedCustomer.id,
        facilityId: selectedFacility.id,
        workOrderId,
        procedureId,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          "work-order-procedure",
          selectedCustomer?.id,
          selectedFacility?.id,
          workOrderId,
        ],
      });
    },
  });
}

export function useUpdateProcedureAttachmentMutation(workOrderId: WorkOrderId) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      procedureId: string | null;
      discardResponses: boolean;
      expectedAttachmentId: number | null;
    }) => {
      if (!selectedCustomer || !selectedFacility)
        throw new Error("A customer and facility must be selected.");
      return updateProcedureAttachment({
        customerId: selectedCustomer.id,
        facilityId: selectedFacility.id,
        workOrderId,
        ...input,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          "work-order-procedure",
          selectedCustomer?.id,
          selectedFacility?.id,
          workOrderId,
        ],
      });
      void queryClient.invalidateQueries({
        queryKey: [
          "work-order-procedure-execution",
          selectedCustomer?.id,
          selectedFacility?.id,
          workOrderId,
        ],
      });
    },
  });
}
